import HealthSourceInfo, { HealthSourcesLink } from "@/components/health/HealthSourceInfo";
import { useMemo } from "react";
import { UtensilsCrossed, ChevronRight, Flame } from "lucide-react";

export default function DailyNutritionCard({
  consumedCalories = 0,
  targetCalories = 2000,
  consumedProtein = 0,
  targetProtein = 150,
  consumedCarbs = 0,
  targetCarbs = 200,
  consumedFats = 0,
  targetFats = 60,
  onOpenPreview,
}) {
  const calPercent = targetCalories > 0
    ? Math.min(100, Math.round((consumedCalories / targetCalories) * 100))
    : 0;

  const proteinPercent = targetProtein > 0
    ? Math.min(100, Math.round((consumedProtein / targetProtein) * 100))
    : 0;

  const carbsPercent = targetCarbs > 0
    ? Math.min(100, Math.round((consumedCarbs / targetCarbs) * 100))
    : 0;

  const fatsPercent = targetFats > 0
    ? Math.min(100, Math.round((consumedFats / targetFats) * 100))
    : 0;

  return (
    <div
      data-testid="daily-nutrition-card"
      className="rounded-3xl p-5 sm:p-6 text-white space-y-4 shadow-xl border border-white/10"
      style={{
        background: "radial-gradient(ellipse at top, #0F1B2E 0%, #070D18 100%)",
      }}
    >
      {/* SECTION HEADER: TITLE & PREVIEW MEALS ACTION */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-sky-400">
            NUTRITION & MACROS
          </p>
          <h3 className="text-xl sm:text-2xl font-display font-black text-white tracking-tight">
            Daily Nutrition
          </h3>
        </div>

        <button
          type="button"
          onClick={onOpenPreview}
          data-testid="preview-meals-btn"
          className="px-3.5 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-xs font-bold text-sky-300 flex items-center gap-1.5 transition active:scale-95"
        >
          <span>Preview Meals</span>
          <ChevronRight className="w-3.5 h-3.5 opacity-70" />
        </button>
      </div>

      <HealthSourcesLink />
      {/* CALORIES PROGRESS BAR */}
      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between text-xs">
          <span className="text-slate-400 font-semibold flex items-center gap-1">
            <Flame className="w-3.5 h-3.5 text-amber-400" />
            <span>Calories</span><HealthSourceInfo topic="dashboard" label="Daily calorie target and sources" />
          </span>
          <div>
            <span className="text-base sm:text-lg font-black text-white">
              {consumedCalories.toLocaleString()}
            </span>
            <span className="text-slate-400 font-medium ml-1">
              / {targetCalories.toLocaleString()} kcal
            </span>
          </div>
        </div>

        {/* Clean Linear Progress Bar */}
        <div className="h-2.5 w-full bg-white/10 rounded-full overflow-hidden p-0.5">
          <div
            className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 transition-all duration-500 shadow-[0_0_12px_rgba(56,189,248,0.5)]"
            style={{ width: `${calPercent}%` }}
          />
        </div>
      </div>

      {/* 3-COLUMN MACROS ROW */}
      <div className="grid grid-cols-3 gap-2 sm:gap-2.5 pt-1">
        {/* Protein */}
        <div className="p-1.5 min-[360px]:p-2 sm:p-3 min-w-0 rounded-2xl bg-white/[0.04] border border-white/5 space-y-1.5">
          <div className="flex items-center justify-between text-[11px]">
            <span className="inline-flex items-center whitespace-nowrap font-extrabold text-emerald-400 uppercase tracking-normal sm:tracking-wide text-[9px] min-[360px]:text-[10px] sm:text-[11px]">
              Protein<HealthSourceInfo compact topic="dashboard" label="Protein target and sources" />
            </span>
          </div>
          <div className="text-xs font-bold text-white">
            <span>{Math.round(consumedProtein)}g</span>
            <span className="text-slate-400 font-normal text-[10px] ml-1">
              / {targetProtein}g
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 min-w-0 flex-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-400 rounded-full transition-all duration-500" style={{ width: `${proteinPercent}%` }} />
            </div>
            <span className="shrink-0 whitespace-nowrap font-mono text-slate-400 text-[9px]">{proteinPercent}%</span>
          </div>
        </div>

        {/* Carbs */}
        <div className="p-1.5 min-[360px]:p-2 sm:p-3 min-w-0 rounded-2xl bg-white/[0.04] border border-white/5 space-y-1.5">
          <div className="flex items-center justify-between text-[11px]">
            <span className="inline-flex items-center whitespace-nowrap font-extrabold text-sky-400 uppercase tracking-normal sm:tracking-wide text-[9px] min-[360px]:text-[10px] sm:text-[11px]">
              Carbs<HealthSourceInfo compact topic="dashboard" label="Carbs target and sources" />
            </span>
          </div>
          <div className="text-xs font-bold text-white">
            <span>{Math.round(consumedCarbs)}g</span>
            <span className="text-slate-400 font-normal text-[10px] ml-1">
              / {targetCarbs}g
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 min-w-0 flex-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-sky-400 rounded-full transition-all duration-500" style={{ width: `${carbsPercent}%` }} />
            </div>
            <span className="shrink-0 whitespace-nowrap font-mono text-slate-400 text-[9px]">{carbsPercent}%</span>
          </div>
        </div>

        {/* Fat */}
        <div className="p-1.5 min-[360px]:p-2 sm:p-3 min-w-0 rounded-2xl bg-white/[0.04] border border-white/5 space-y-1.5">
          <div className="flex items-center justify-between text-[11px]">
            <span className="inline-flex items-center whitespace-nowrap font-extrabold text-amber-400 uppercase tracking-normal sm:tracking-wide text-[9px] min-[360px]:text-[10px] sm:text-[11px]">
              Fat<HealthSourceInfo compact topic="dashboard" label="Fat target and sources" />
            </span>
          </div>
          <div className="text-xs font-bold text-white">
            <span>{Math.round(consumedFats)}g</span>
            <span className="text-slate-400 font-normal text-[10px] ml-1">
              / {targetFats}g
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 min-w-0 flex-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${fatsPercent}%` }} />
            </div>
            <span className="shrink-0 whitespace-nowrap font-mono text-slate-400 text-[9px]">{fatsPercent}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
